import { Modifier, EditorState, RichUtils, SelectionState } from 'draft-js';
import { isUrl } from '../../../helpers/isUrl'

const insertText = ({
  editorState,
  selection,
  textToInsert,
  removeOldEntity = false,
}) => {
  const currentContent = editorState.getCurrentContent();

  const content = removeOldEntity
    ? Modifier.applyEntity(currentContent, selection, null)
    : currentContent;

  const newContent = Modifier.replaceText(content, selection, textToInsert);

  const newEditorState = EditorState.push(
    editorState,
    newContent,
    'insert-characters'
  );

  return newEditorState;
};

const insertTextWithLink = ({
  editorState,
  selection,
  textToInsert,
  url,
  removeOldEntity = false,
}) => {
  let newEditorState = insertText({
    editorState,
    selection,
    textToInsert,
    removeOldEntity,
  });

  const contentWithLinkEntity = newEditorState
    .getCurrentContent()
    .createEntity('LINK', 'MUTABLE', { url });

  const linkEntityKey = contentWithLinkEntity.getLastCreatedEntityKey();

  newEditorState = EditorState.set(newEditorState, {
    currentContent: contentWithLinkEntity,
  });

  const newSelection = newEditorState.getSelection().merge({
    anchorOffset: selection.getStartOffset(),
    focusOffset: selection.getStartOffset() + textToInsert.length,
  });

  newEditorState = RichUtils.toggleLink(
    newEditorState,
    newSelection,
    linkEntityKey
  );

  const endSelection = new SelectionState({
    anchorKey: newSelection.getAnchorKey(),
    anchorOffset: newSelection.getEndOffset(),
    focusKey: newSelection.getFocusKey(),
    focusOffset: newSelection.getEndOffset(),
    hasFocus: true,
  });

  const contentWithExtraSpace = Modifier.insertText(
    newEditorState.getCurrentContent(),
    endSelection,
    '\u200B'
  );

  newEditorState = EditorState.push(
    newEditorState,
    contentWithExtraSpace,
    'insert-characters'
  );

  newEditorState = EditorState.acceptSelection(newEditorState, endSelection);

  return newEditorState;
};

const findLinkEntityRange = (block, contentState, selection) => {
  let linkRange = null;

  block.findEntityRanges(
    (character) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'LINK'
      );
    },
    (start, end) => {
      if (
        selection.getStartOffset() >= start &&
        selection.getStartOffset() <= end
      ) {
        linkRange = new SelectionState({
          anchorKey: block.getKey(),
          anchorOffset: start,
          focusKey: block.getKey(),
          focusOffset: end,
        });
      }
    }
  );

  return linkRange;
};

export const addLink = ({ editorState, textToInsert, url }) => {
  const selection = editorState.getSelection();
  return insertTextWithLink({
    editorState,
    selection,
    textToInsert,
    url,
    removeOldEntity: false,
  });
};

export const editLink = ({ editorState, textToInsert, url }) => {
  const selection = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();

  const blockWithLinkAtBeginning = currentContent.getBlockForKey(
    selection.getStartKey()
  );

  const linkRange = findLinkEntityRange(
    blockWithLinkAtBeginning,
    currentContent,
    selection
  );

  if (linkRange) {
    return insertTextWithLink({
      editorState,
      selection: linkRange,
      textToInsert,
      url,
      removeOldEntity: true,
    });
  }

  return editorState;
};

export const removeLinkFromText = ({ editorState }) => {
  const selection = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();

  const blockWithLinkAtBeginning = currentContent.getBlockForKey(
    selection.getStartKey()
  );

  const linkRange = findLinkEntityRange(
    blockWithLinkAtBeginning,
    currentContent,
    selection
  );

  if (linkRange) {
    const contentWithoutLinkEntity = Modifier.applyEntity(
      currentContent,
      linkRange,
      null
    );

    let newEditorState = EditorState.set(editorState, {
      currentContent: contentWithoutLinkEntity,
    });

    return newEditorState;
  }

  return editorState;
};

export const turnWordIntoLink = ({ editorState, endChar, onChange }) => {
  const selection = editorState.getSelection();
  const anchorKey = selection.getAnchorKey();
  const content = editorState.getCurrentContent();
  const currentBlock = content.getBlockForKey(anchorKey);
  const blockText = currentBlock.getText();
  const start = selection.getStartOffset();
  const end = selection.getEndOffset();

  if (blockText[start] !== ' ' && blockText[start] !== undefined) {
    return 'not-handled';
  }

  let wordStart = start;
  while (wordStart > 0 && blockText[wordStart - 1] !== ' ') {
    wordStart--;
  }

  const word = blockText.slice(wordStart, end);
  if (isUrl(word)) {
    const linkSelection = selection.merge({
      anchorOffset: wordStart,
      focusOffset: end,
    });

    const linkEntity = content.createEntity('LINK', 'MUTABLE', {
      url: word,
    });
    const linkEntityKey = linkEntity.getLastCreatedEntityKey();
    const contentWithLink = Modifier.applyEntity(
      content,
      linkSelection,
      linkEntityKey
    );

    const endCharSelection = linkSelection.merge({
      anchorOffset: start,
      focusOffset: start,
    });

    const contentWithLinkAndEndChar = Modifier.insertText(
      contentWithLink,
      endCharSelection,
      endChar
    );

    const newEditorState = EditorState.push(
      editorState,
      contentWithLinkAndEndChar,
      'insert-characters'
    );
    onChange(newEditorState);
    return 'handled';
  }

  return 'not-handled';
};
